import React, { useCallback, useMemo } from 'react';
import { ActivityItem } from './ActivityItem';
import { ICrmActivity } from 'services/CrmApi/types/CrmActivityTypes';
import { LoadingBar } from 'ui/NetworkStatusBar';
import SingleSelect, { ISingleSelectOption } from 'ui/SingleSelect';
import { ESortOrder } from 'store/common/types';
import FluidButton from 'ui/FluidButton';
import { getActivitiesGroupedByMonth } from './activityGrouping';
import { ECrmDirectoryEntryType } from 'services/CrmApi/types/DirectoryEntry';

interface IActivityGroupHeaderProps {
  text: string;
  shouldShowSorting: boolean;
  sortOrder?: ESortOrder;
  onSortOrderChange?: (sortOrder: ESortOrder) => void;
}

export const ActivityGroupHeader: React.FC<IActivityGroupHeaderProps> = ({
  text,
  shouldShowSorting,
  sortOrder,
  onSortOrderChange,
}) => {
  const sortingOptions: ISingleSelectOption[] = useMemo(() => {
    return [
      {
        label: 'Newest first',
        value: ESortOrder.DESC,
      },
      {
        label: 'Oldest first',
        value: ESortOrder.ASC,
      },
    ];
  }, []);

  return (
    <div className="activity-group-header flex items-center justify-between mb-[5px]">
      <p className="font-pt-sans text-[17px] font-[700] text-black capitalize m-0">{text}</p>
      {shouldShowSorting ? (
        <SingleSelect
          fieldId="activities-sort-order"
          className="activities-sort-order w-[160px]"
          labelClassName="text-[16px] leading-[21px] font-bold"
          value={sortOrder}
          options={sortingOptions}
          onChange={onSortOrderChange}
          maxVisibleItems={4}
        />
      ) : null}
    </div>
  );
};

interface IActivitiesLoadMoreProps {
  onLoadMoreClick: () => void;
  isLoading: boolean;
}

export const ActivitiesLoadMore: React.FC<IActivitiesLoadMoreProps> = ({ onLoadMoreClick, isLoading }) => {
  const handleLoadMore = useCallback(() => {
    onLoadMoreClick();
  }, [onLoadMoreClick]);

  return (
    <FluidButton type="secondary" onClick={handleLoadMore} isLoading={isLoading}>
      Load More
    </FluidButton>
  );
};

interface IActivityListProps {
  activities: ICrmActivity[];
  count: number;
  highlightedActivityId: string | null;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  sortOrder?: ESortOrder;
  directoryEntryType?: ECrmDirectoryEntryType;
  directoryEntryId?: string;
  onSortOrderChange?: (sortOrder: ESortOrder) => void;
  onEditActivity: () => void;
  onDeleteActivity: () => void;
}

export const ActivityList: React.FC<IActivityListProps> = ({
  activities,
  count,
  highlightedActivityId,
  isLoading,
  isFetchingNextPage,
  onLoadMore: onLoadMoreClick,
  sortOrder,
  directoryEntryType,
  directoryEntryId,
  onSortOrderChange,
  onEditActivity,
  onDeleteActivity,
}) => {
  if (isLoading && !isFetchingNextPage) {
    return (
      <div className="mt-[45px] mb-[50px]">
        <LoadingBar />
      </div>
    );
  }

  if (activities.length === 0) {
    return <p className="m-0 font-pt-sans text-[15px] text-black italic">No Results Found.</p>;
  }

  const groupedActivities = getActivitiesGroupedByMonth(activities);

  return (
    <div className="crm-activity-list flex flex-col gap-[5px]">
      {Object.keys(groupedActivities).map((month, index) => {
        return (
          <div key={month} className="activity-list">
            <ActivityGroupHeader
              text={month}
              shouldShowSorting={index === 0}
              sortOrder={sortOrder}
              onSortOrderChange={onSortOrderChange}
            />
            <div className="flex flex-col gap-[5px]">
              {groupedActivities[month].map(item => {
                return (
                  <ActivityItem
                    key={item.id}
                    type={item.type}
                    activity={item}
                    isHighlighted={item.id === highlightedActivityId}
                    onEditActivity={onEditActivity}
                    onDeleteActivity={onDeleteActivity}
                    directoryEntryId={directoryEntryId}
                    directoryEntryType={directoryEntryType}
                  />
                );
              })}
            </div>
          </div>
        );
      })}

      <div className="activity-load-more flex flex-col items-center mt-[20px]">
        {activities.length < count ? (
          <ActivitiesLoadMore onLoadMoreClick={onLoadMoreClick} isLoading={isFetchingNextPage} />
        ) : null}
        <p className="font-pt-sans text-[13px] tracking-[0.52px] text-gray-100 m-0 mt-[10px]">
          Showing {activities.length} of {count} results
        </p>
      </div>
    </div>
  );
};
